import * as Tone from 'tone';

export type SynthTypeId =
  | 'synth'
  | 'monosynth'
  | 'amsynth'
  | 'fmsynth'
  | 'duosynth'
  | 'membrane'
  | 'metal'
  | 'noise'
  | 'pluck';

export interface ExtraParam {
  readonly key: string;
  readonly label: string;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly default: number;
  readonly format?: (v: number) => string;
}

/** Any Tone instrument constructor, narrowed at each call site. */
export type ToneInstrumentCtor = new (opts?: object) => Tone.ToneAudioNode;

export interface SynthTypeDef {
  readonly label: string;
  /** Constructor on the Tone namespace. */
  readonly ctor: () => ToneInstrumentCtor;
  /** Whether the voice extends Tone's Monophonic and can go inside a PolySynth. */
  readonly poly: boolean;
  readonly hasOscillator: boolean;
  /** false = no envelope at all, 'percussive' = no sustain stage. */
  readonly envelope: true | false | 'percussive';
  /** Needs an AudioWorklet, which a file:// page cannot load. */
  readonly needsWorklet?: boolean;
  readonly extra: readonly ExtraParam[];
}

export const SYNTH_TYPES: Readonly<Record<SynthTypeId, SynthTypeDef>> = {
  synth: {
    label: 'Synth',
    ctor: () => Tone.Synth as never,
    poly: true,
    hasOscillator: true,
    envelope: true,
    extra: [],
  },
  monosynth: {
    label: 'MonoSynth',
    ctor: () => Tone.MonoSynth as never,
    poly: true,
    hasOscillator: true,
    envelope: true,
    extra: [],
  },
  amsynth: {
    label: 'AMSynth',
    ctor: () => Tone.AMSynth as never,
    poly: true,
    hasOscillator: true,
    envelope: true,
    extra: [{ key: 'harmonicity', label: 'Armonicitate', min: 0.5, max: 8, step: 0.1, default: 1 }],
  },
  fmsynth: {
    label: 'FMSynth',
    ctor: () => Tone.FMSynth as never,
    poly: true,
    hasOscillator: true,
    envelope: true,
    extra: [
      { key: 'harmonicity', label: 'Armonicitate', min: 0.5, max: 8, step: 0.1, default: 1 },
      { key: 'modulationIndex', label: 'Index modulație', min: 1, max: 20, step: 0.5, default: 10 },
    ],
  },
  duosynth: {
    label: 'DuoSynth',
    ctor: () => Tone.DuoSynth as never,
    poly: true,
    hasOscillator: true,
    envelope: true,
    extra: [
      { key: 'harmonicity', label: 'Armonicitate', min: 0.5, max: 8, step: 0.1, default: 1.5 },
      { key: 'vibratoAmount', label: 'Vibrato', min: 0, max: 1, step: 0.01, default: 0.5 },
    ],
  },
  membrane: {
    // A drum voice. Given a sustaining envelope every note becomes a drone,
    // so it is forced percussive.
    label: 'MembraneSynth',
    ctor: () => Tone.MembraneSynth as never,
    poly: true,
    hasOscillator: true,
    envelope: 'percussive',
    extra: [
      { key: 'pitchDecay', label: 'Pitch decay (s)', min: 0.01, max: 0.5, step: 0.01, default: 0.05 },
      { key: 'octaves', label: 'Octave', min: 0.5, max: 10, step: 0.1, default: 6 },
    ],
  },
  metal: {
    label: 'MetalSynth',
    ctor: () => Tone.MetalSynth as never,
    poly: true,
    hasOscillator: false,
    envelope: 'percussive',
    extra: [
      { key: 'harmonicity', label: 'Armonicitate', min: 0.5, max: 8, step: 0.1, default: 5.1 },
      { key: 'modulationIndex', label: 'Index modulație', min: 1, max: 64, step: 1, default: 32 },
      { key: 'resonance', label: 'Rezonanță (Hz)', min: 1000, max: 8000, step: 50, default: 4000 },
    ],
  },
  noise: {
    label: 'NoiseSynth (monofonic)',
    ctor: () => Tone.NoiseSynth as never,
    poly: false,
    hasOscillator: false,
    envelope: 'percussive',
    extra: [
      {
        key: 'noiseType',
        label: 'Culoare zgomot',
        min: 0,
        max: 2,
        step: 1,
        default: 0,
        format: (v) => ['Alb', 'Roz', 'Maro'][Math.round(v)] ?? 'Alb',
      },
    ],
  },
  pluck: {
    // Tone builds PluckSynth's comb filter as an AudioWorklet.
    // Resonance at 0.99 puts the Karplus-Strong loop on the edge of
    // self-oscillation and it never decays, so it is capped below that.
    label: 'PluckSynth (monofonic)',
    ctor: () => Tone.PluckSynth as never,
    poly: false,
    hasOscillator: false,
    envelope: false,
    needsWorklet: true,
    extra: [
      { key: 'dampening', label: 'Amortizare (Hz)', min: 100, max: 7000, step: 50, default: 4000 },
      { key: 'resonance', label: 'Rezonanță', min: 0, max: 0.95, step: 0.01, default: 0.9 },
    ],
  },
};

export const SYNTH_TYPE_IDS = Object.keys(SYNTH_TYPES) as SynthTypeId[];

export type ExtraValues = Record<SynthTypeId, Record<string, number>>;

export const defaultExtraValues = (): ExtraValues => {
  const out = {} as ExtraValues;
  for (const id of SYNTH_TYPE_IDS) {
    const vals: Record<string, number> = {};
    for (const p of SYNTH_TYPES[id].extra) vals[p.key] = p.default;
    out[id] = vals;
  }
  return out;
};

export type Waveform = 'sine' | 'triangle' | 'sawtooth' | 'square';

/**
 * Perceived-loudness trim per waveform: a sine at the same peak amplitude
 * sounds far quieter than a sawtooth. Folded into note velocity, which is
 * always kept inside 0..1.
 */
export const WAVEFORM_GAIN: Readonly<Record<Waveform, number>> = {
  sine: 2.4,
  triangle: 1.5,
  sawtooth: 1.0,
  square: 0.85,
};

export interface WaveformButton {
  readonly id: Waveform;
  readonly name: string;
  /** SVG path drawing the wave shape, in a 0 0 22 14 viewBox. */
  readonly path: string;
}

/** The waveform chooser, rendered as small icon buttons of the wave shapes. */
export const WAVEFORMS: readonly WaveformButton[] = [
  { id: 'sine', name: 'Sinusoidal', path: 'M1 7 Q 4.25 1 7.5 7 T 14 7 T 20.5 7' },
  { id: 'triangle', name: 'Triunghiular', path: 'M1 12 L 6 2 L 11 12 L 16 2 L 21 12' },
  { id: 'sawtooth', name: 'Dinte de fierăstrău', path: 'M1 12 L 8 2 L 8 12 L 15 2 L 15 12 L 21 3' },
  { id: 'square', name: 'Pătrat', path: 'M1 12 L 1 2 L 8 2 L 8 12 L 15 12 L 15 2 L 21 2' },
];
